import { useCallback } from 'react';

import { IApplePayCartEntry } from 'features/payment/types';

import { t } from 'state/intl/translations';

import { ISOs, getCountryAndCurrencyCodes } from '../../../utils/form/constants';
import logger from '../../../utils/logger';

import { IUseApplePay } from './types';

export type ApplePayCartSource = 'checkout' | 'legacy';

type UsableApplePayCartEntry = IApplePayCartEntry & {
  name: string;
  price: number;
};

const isUsableApplePayCartEntry = (entry: IApplePayCartEntry): entry is UsableApplePayCartEntry =>
  typeof entry.name === 'string' &&
  entry.name.trim().length > 0 &&
  typeof entry.price === 'number' &&
  Number.isFinite(entry.price) &&
  entry.price >= 0 &&
  Number.isFinite(entry.quantity) &&
  entry.quantity > 0;

const useFormatApplePayCartPayload = ({
  serverOrder,
  tipAmount,
  billingCountry,
  cartEntries,
  cartSource,
}: Pick<IUseApplePay, 'serverOrder' | 'tipAmount'> & {
  billingCountry: ISOs;
  cartEntries: IApplePayCartEntry[];
  cartSource: ApplePayCartSource;
}) => {
  const formatApplePayCartPayload = useCallback(() => {
    const usableCartEntries = cartEntries?.filter(isUsableApplePayCartEntry) ?? [];
    const cartEntriesAvailable = Boolean(cartEntries?.length);
    const cartEntriesValid =
      cartEntriesAvailable && usableCartEntries.length === cartEntries.length;

    if (!(cartEntriesValid && serverOrder && serverOrder.cart)) {
      logger.error({
        isPaymentLog: true,
        message: 'Missing values to format apple pay cart payload.',
        cartSource,
        cartEntriesAvailable,
        cartEntriesValid,
        serverOrderAvailable: Boolean(serverOrder),
        serverOrderCartAvailable: Boolean(serverOrder?.cart),
      });
      return null;
    }

    const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);

    // send the cart items info to applePay, subtotal and tax to applePay summary
    const cartLabels: string[] = [];
    const cartPrices: number[] = [];
    usableCartEntries.forEach(item => {
      cartLabels.push(`${item.quantity} x ${item.name}`);
      cartPrices.push(item.price / 100);
    });
    const { subTotalCents, taxCents, totalCents } = serverOrder.cart;
    // Capitalize the taxes text
    const taxesText = t('taxes');
    const capitalizeTaxesText = taxesText[0].toUpperCase() + taxesText.slice(1);
    cartLabels.push(t('subtotal'), capitalizeTaxesText);
    cartPrices.push(subTotalCents / 100, taxCents / 100);

    // Show the delivery fee if applicable
    const deliveryFeeCents = serverOrder?.delivery?.feeCents ?? 0;
    const deliveryFeeDiscountCents = serverOrder?.delivery?.feeDiscountCents ?? 0;
    if (deliveryFeeCents - deliveryFeeDiscountCents) {
      cartLabels.push(t('deliveryFee'));
      cartPrices.push((deliveryFeeCents ?? 0) / 100);
    }
    // Add tip if applicable
    if (tipAmount) {
      cartLabels.push(t('tip'));
      cartPrices.push((tipAmount ?? 0) / 100);
    }

    return {
      countryCode,
      currencyCode,
      total: totalCents + (tipAmount ?? 0),
      cartLabels,
      cartPrices,
    };
  }, [billingCountry, cartEntries, cartSource, serverOrder, tipAmount]);

  return { formatApplePayCartPayload };
};

export default useFormatApplePayCartPayload;
